import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, ChevronDown, ChevronRight, Clock, UserCheck, MessageCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const classLabel = (c) => {
  if (["LKG", "UKG", "Play Group"].includes(c)) return c;
  const n = parseInt(c, 10);
  if (isNaN(n)) return c;
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const fmtDue = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };
const pctTone = (p) => (p < 50 ? "text-rose-600 dark:text-rose-400" : p <= 70 ? "text-sky-600 dark:text-sky-400" : "text-emerald-600 dark:text-emerald-300");
const barTone = (p) => (p < 50 ? "bg-rose-500" : p <= 70 ? "bg-sky-500" : "bg-emerald-500");

export default function ClassTests() {
  const [tests, setTests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/teacher/class-tests")
      .then(({ data }) => setTests(data.tests || []))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail) || "Could not load class tests"); setTests([]); })
      .finally(() => setLoading(false));
  }, []);

  const subjects = useMemo(() => {
    if (!tests) return [];
    return [...new Set(tests.map((t) => t.subject).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [tests]);

  const filtered = useMemo(() => {
    if (!tests) return [];
    return subjectFilter === "all" ? tests : tests.filter((t) => t.subject === subjectFilter);
  }, [tests, subjectFilter]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const remind = () => toast.success("WhatsApp reminder coming soon!");

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="class-tests-page">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Class teacher</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
            <ClipboardCheck className="inline h-7 w-7 text-indigo-500 mr-2 -mt-1" /> Class tests
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Every test given to your class by subject teachers, and who hasn&apos;t attempted on time.</p>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="text-xs text-slate-400" data-testid="class-tests-count">
            {loading ? "Loading…" : `${filtered.length} test${filtered.length === 1 ? "" : "s"}`}
          </div>
          <div className="w-44">
            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger data-testid="class-tests-subject-filter"><SelectValue placeholder="All subjects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-12 text-center" data-testid="class-tests-empty">
            <ClipboardCheck className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="font-medium text-slate-900 dark:text-slate-100">No tests yet</div>
            <div className="text-slate-500 text-sm mt-1">Published tests for your class will appear here.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="class-tests-list">
            {filtered.map((t) => {
              const isOpen = !!expanded[t.id];
              const missing = t.not_attempted.length;
              return (
                <div key={t.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden" data-testid={`class-test-${t.id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{t.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-300 text-[10px] font-semibold uppercase">{t.subject || "—"}</span></span>
                          <span className="inline-flex items-center gap-1"><UserCheck className="h-3.5 w-3.5 text-slate-300" /> {t.teacher_name || "—"}</span>
                          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-300" /> Due {fmtDue(t.due_at)}</span>
                          {t.locked && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">Closed</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-2xl font-bold ${pctTone(t.percentage)}`} style={{ fontFamily: "Outfit" }} data-testid={`class-test-pct-${t.id}`}>{t.percentage}%</div>
                        <div className="text-[11px] text-slate-400">{t.attempted}/{t.total} attempted</div>
                      </div>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div className={`h-full rounded-full ${barTone(t.percentage)}`} style={{ width: `${Math.min(100, t.percentage)}%` }} />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      {missing === 0 ? (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Everyone attempted on time</span>
                      ) : (
                        <button type="button" onClick={() => toggle(t.id)} className="text-xs font-medium text-rose-600 dark:text-rose-400 inline-flex items-center gap-1" data-testid={`class-test-toggle-${t.id}`}>
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {missing} didn&apos;t attempt on time
                        </button>
                      )}
                      {missing > 0 && (
                        <Button size="sm" variant="outline" onClick={remind} className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400" data-testid={`class-test-remind-${t.id}`}>
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Remind by WhatsApp
                        </Button>
                      )}
                    </div>
                  </div>

                  {isOpen && missing > 0 && (
                    <ul className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700" data-testid={`class-test-missing-${t.id}`}>
                      {t.not_attempted.map((s) => (
                        <li key={s.student_id} className="flex items-center justify-between gap-3 px-5 py-2.5" data-testid={`class-test-missing-student-${s.student_id}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name}</div>
                            <div className="text-[11px] text-slate-400 font-mono">#{s.roll_number || "—"}</div>
                          </div>
                          {s.late ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Submitted late</span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not attempted</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
